/**
 * useAIContentTypes
 * Provides two independent fetch functions:
 *  - `fetchRegisteredOptions` – base types with a registered prompt form (AI dropdown)
 *  - `fetchAllContentTypes`  – non-base AIContentType rows (Quick Create / templates)
 *
 * Both hit the same API endpoint but derive different result sets.
 * Call them explicitly instead of relying on useEffect.
 */

import { useState, useCallback, useRef } from 'react';
import {
  aiContentTypeService,
  questionTypeGroupService,
  type QuestionTypeGroup,
} from '@ai-content-hub/shared-api';
import {
  getCanonicalSlugs,
  getRegisteredFormDisplayInfo,
  filterQuickCreateOptions,
  getQuestionTypeLabel,
  getPromptLocale,
} from '@ai-content-hub/content-prompt';
import type { QuestionTypeOption } from './AIContentGenerator.types';

function mapToOptions(results: Array<{ id: string; slug: string; code?: string; name: string; description?: string; category?: string }>): QuestionTypeOption[] {
  return results.map(ct => ({
    id: ct.id,
    slug: ct.slug,
    code: ct.code,
    name: ct.name,
    description: ct.description,
    category: ct.category,
  }));
}

export function useAIContentTypes() {
  const [options, setOptions] = useState<QuestionTypeOption[]>([]);
  const [allOptions, setAllOptions] = useState<QuestionTypeOption[]>([]);
  // EAA-2026: the tenant's question-type groups that head the Quick Create list.
  const [questionTypeGroups, setQuestionTypeGroups] = useState<QuestionTypeGroup[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef<{ registered: boolean; all: boolean }>({ registered: false, all: false });

  const fetchRegisteredOptions = useCallback(async () => {
    if (fetchedRef.current.registered) return;
    fetchedRef.current.registered = true;
    setLoading(true);
    setError(null);
    try {
      const locale = getPromptLocale();
      const res = await aiContentTypeService.listAIContentTypes();
      const canonicalSlugs = new Set(getCanonicalSlugs());
      const displayBySlug = new Map(
        getRegisteredFormDisplayInfo(locale).map((d) => [d.slug, d])
      );
      const all = mapToOptions(res.results);
      const filtered = all.filter(
        (ct) =>
          canonicalSlugs.has(ct.slug) ||
          (ct.code != null && canonicalSlugs.has(ct.code)),
      );

      // EMG-GQ-ENUM-CATALOG: prefer shared bilingual labels; normalize slug to enum.
      for (const opt of filtered) {
        if (!canonicalSlugs.has(opt.slug) && opt.code && canonicalSlugs.has(opt.code)) {
          opt.slug = opt.code;
        }
        opt.name = getQuestionTypeLabel(opt.slug, locale);
        const display = displayBySlug.get(opt.slug);
        if (!opt.name && display?.name) {
          opt.name = display.name;
        }
      }

      const apiSlugs = new Set(filtered.map(o => o.slug));
      const fallbacks = getRegisteredFormDisplayInfo(locale);
      for (const fb of fallbacks) {
        if (!apiSlugs.has(fb.slug)) {
          filtered.push({
            id: fb.slug,
            slug: fb.slug,
            name: fb.name,
            description: fb.description,
            category: fb.category,
          });
        }
      }

      setOptions(filtered);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load registered types';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllContentTypes = useCallback(async () => {
    if (fetchedRef.current.all) return;
    fetchedRef.current.all = true;
    setLoading(true);
    setError(null);
    try {
      // WHY: Quick Create lists product/template AI types only; base forms stay
      // in the AI dropdown via fetchRegisteredOptions (no duplicate “Drag the Words”).
      // EAA-2026: the group catalog loads alongside; a failure (no acting tenant, no
      // permission) only costs the headings — every type still lists under "Khác".
      void questionTypeGroupService
        .getCatalog()
        .then((catalog) => setQuestionTypeGroups(catalog.groups))
        .catch(() => setQuestionTypeGroups(undefined));
      const res = await aiContentTypeService.listAIContentTypes();
      setAllOptions(filterQuickCreateOptions(mapToOptions(res.results)));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load content types';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    options,
    allOptions,
    questionTypeGroups,
    loading,
    error,
    fetchRegisteredOptions,
    fetchAllContentTypes,
  };
}
